import React, { useRef } from 'react';
import ProfileNav from './ProfileNav';
import {
  shopBarberImageUpload,
  barberProfileImageUpload,
  shopProfileImageUpload,
} from '../../middleware/registration_service/updateRequest';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import Fab from '@mui/material/Fab';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import ShopProfileDialog from '../components/ShopNameDialog';
import ShopAddressDialog from '../components/ShopAddressDialog';
import BarberAddressDialog from '../components/BarberAddressDialog';
import { AxiosResponse } from 'axios';
import { UserRole } from '../../types/role';
import { useProfileData } from '../../context/ProfileDataContext';
import { useAuth0 } from '@auth0/auth0-react';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export default function Profile() {
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const { role, shopId, barberId, shop, barber, setShop, setBarber } =
    useProfileData();
  const { getAccessTokenSilently } = useAuth0();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectProfileHeaderImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files as FileList;

    if (selectedFiles) {
      try {
        const token = await getAccessTokenSilently();
        await shopProfileImageUpload(selectedFiles?.[0], shopId, token).then(
          (response: AxiosResponse) => {
            setShop(response.data);
          }
        );
      } catch (error: any) {
        alert(`Error: ${error.response.data}`);
      }
    } else {
      alert('Please select a file.');
    }
  };

  const selectProfileImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = event.target.files as FileList;

    if (selectedFiles) {
      try {
        const token = await getAccessTokenSilently();
        if (role === UserRole.BARBER) {
          await barberProfileImageUpload(
            selectedFiles?.[0],
            barberId,
            token
          ).then((response) => {
            setBarber(response.data);
          });
        } else if (role === UserRole.SHOP_BARBER) {
          await shopBarberImageUpload(
            selectedFiles?.[0],
            shopId,
            barberId,
            token
          ).then((response) => {
            setShop(response.data);
          });
        }
      } catch (error: any) {
        alert(`Error: ${error.response.data}`);
      }
    } else {
      alert('Please select a file.');
    }
  };

  return (
    <Container sx={{ padding: '0 !important' }}>
      <Container
        component="div"
        className="profile"
        sx={{
          height: 'auto',
          backgroundColor: 'white',

          width: '100%',
          margin: '0 !important',
          padding: '0 !important',
        }}
      >
        <Container
          component="div"
          className="container-fluid"
          sx={{
            backgroundImage: ` url(${shop?.shopProfileImageLink}) `,
            backgroundColor: 'white !important',
            width: '100%',
            height: '404px',
            margin: '0px !important',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            padding: '0 !important',
          }}
        >
          <Container
            component="div"
            sx={{
              width: '100%',
              height: '404px',
              margin: '0px !important',
              padding: '0 !important',
              backgroundPosition: 'center',
              backdropFilter: 'blur(35px)',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <Link
              href={undefined}
              sx={{
                display: 'flex',
                position: 'relative',
                alignContent: 'right',
                justifyContent: 'right',
                top: '20px',
                marginRight: '12px',
                visibility: role === UserRole.SHOP_OWNER ? 'visible' : 'hidden',
              }}
            >
              <Fab
                color="primary"
                aria-label="upload"
                sx={{
                  border: '2px solid white',
                  background: '#fffceb',
                  width: '40px',
                  height: '40px',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CameraAltIcon sx={{ width: '20px', height: '20px' }} />
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type={'file'}
                  onChange={selectProfileHeaderImage}
                />
              </Fab>
            </Link>

            <Container
              component="div"
              className="backgroundImg container-fluid"
              sx={{
                backgroundColor: '#dd8709',
                height: '404px',
                width: '100%',
                padding: '0 !important',
                margin: '0 !important',
                position: 'relative',
                bottom: '40px',
              }}
            >
              <img
                className="backgroundPic img-fluid"
                id="backgroundProfile"
                src={
                  role === UserRole.BARBER
                    ? 'https://storage.googleapis.com/barbers-limited-public-images/barbers-limited-text-logo.png'
                    : shop?.shopProfileImageLink
                }
                width="100%"
                alt="profile image."
              />
            </Container>
            {role === UserRole.BARBER ? (
              /* ── Barber avatar: circular, bottom-left of banner, with
                   address displayed to its right ── */
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -44,
                  left: 24,
                  right: 24,
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: 88,
                    height: 88,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    id="profilePic"
                    src={barber?.profileImageSelfLink}
                    alt={user?.name}
                    sx={{
                      width: 88,
                      height: 88,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid white',
                      boxShadow: '0 4px 18px rgba(0,0,0,0.22)',
                      display: 'block',
                      backgroundColor: '#e0e0e0',
                    }}
                  />
                  {/* Mini camera overlay */}
                  <Fab
                    aria-label="upload profile photo"
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      right: -4,
                      width: 28,
                      height: 28,
                      minHeight: 28,
                      background: '#fffceb',
                      border: '2px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      '&:hover': { background: '#fff3cd' },
                    }}
                  >
                    <CameraAltIcon sx={{ width: 14, height: 14 }} />
                    <VisuallyHiddenInput
                      ref={fileInputRef}
                      type="file"
                      onChange={selectProfileImage}
                    />
                  </Fab>
                </Box>

                {/* Independent barber's own address — editable from their profile */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#666',
                      fontFamily: 'GFS Didot',
                      fontSize: '14px',
                    }}
                  >
                    {[
                      barber?.addressLineOne,
                      barber?.addressLineTwo,
                      barber?.city,
                      barber?.postcode,
                      barber?.country,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'No address on file'}
                  </Typography>
                  <BarberAddressDialog />
                </Box>
              </Box>
            ) : (
              <Box sx={{ mt: '100px' }} />
            )}
          </Container>
        </Container>
      </Container>

      {/* Shop name & address — always visible below the banner image */}
      {role !== UserRole.BARBER && (
        <Box
          sx={{
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e0e0e0',
            borderLeft: '4px solid #dd8709',
            px: 4,
            py: 2.5,
          }}
        >
          {/* Shop name row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#1a1a1a',
                fontFamily: 'GFS Didot',
                letterSpacing: '0.5px',
              }}
            >
              {shop?.name}
            </Typography>
            {role === UserRole.SHOP_OWNER && <ShopProfileDialog />}
          </Box>

          {/* Address row */}
          {(shop?.addressLineOne ||
            shop?.city ||
            shop?.country ||
            shop?.postcode) && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
            >
              <Typography
                sx={{
                  color: '#666',
                  fontFamily: 'GFS Didot',
                  fontSize: '14px',
                }}
              >
                {[
                  shop?.addressLineOne,
                  shop?.addressLineTwo,
                  shop?.city,
                  shop?.postcode,
                  shop?.country,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Typography>
              {role === UserRole.SHOP_OWNER && <ShopAddressDialog />}
            </Box>
          )}
        </Box>
      )}

      {/* Spacer so ProfileNav clears the avatar/address row that overlaps
          the bottom of the banner for barber profiles */}
      {role === UserRole.BARBER && <Box sx={{ height: 56 }} />}

      <ProfileNav />
    </Container>
  );
}
